import React from "react";
import cloneDeep from "lodash/cloneDeep";
import { ResolvedBreadcrumb } from "@plugins/gatsby-source-paligo/src/types";
import { Span } from "@singlestore/fusion/components/typography";
import { cva } from "class-variance-authority";

import { Link } from "components/Link/Link";

import "./Breadcrumbs.scss";

const breadcrumbItem = cva("breadcrumb-item", {
    variants: {
        base: {
            true: "base",
        },
        current: {
            true: "current",
        },
    },
});

const insertSeparators = (array: Array<JSX.Element>) =>
    ([] as Array<JSX.Element>)
        .concat(
            ...array.map((breadcrumbLink, index: number) => [
                breadcrumbLink,
                // eslint-disable-next-line react/no-array-index-key
                <li key={`divider_${index}`} role="presentation">
                    /
                </li>,
            ])
        )
        .slice(0, -1);

type BreadcrumbsProps = {
    breadcrumbs: Array<ResolvedBreadcrumb>;
};

export function Breadcrumbs({ breadcrumbs }: BreadcrumbsProps) {
    const _breadcrumbs = cloneDeep(breadcrumbs);
    if (_breadcrumbs.length >= 4) {
        // remove the middle breadcrumbs, leaves 3 elements in the breadcrumbs
        _breadcrumbs.splice(1, _breadcrumbs.length - 3);
    }

    if (_breadcrumbs.length === 3) {
        // change the middle breadcrumb to elipsis
        _breadcrumbs[1].name = "...";
    }

    const breadcrumbList = _breadcrumbs.map(({ name, path }, index) => {
        let breadcrumb = (
            <Link
                to={path}
                className="breadcrumb-link"
                variant="body-2"
                type="secondary"
            >
                {name}
            </Link>
        );

        if (index === _breadcrumbs.length - 1) {
            breadcrumb = (
                <Span
                    variant="heading-1"
                    color="high-contrast"
                    aria-current="page"
                >
                    {name}
                </Span>
            );
        }

        const props: React.HTMLProps<HTMLLIElement> = {};

        if (name === "...") {
            props.role = "presentation";
        }

        const classes = breadcrumbItem({
            base: index === 0,
            current: index === _breadcrumbs.length - 1,
        });

        return (
            <li key={path} className={classes} {...props}>
                {breadcrumb}
            </li>
        );
    });

    const breadcrumbsWithSeparator = insertSeparators(breadcrumbList);

    return (
        <nav aria-label="Breadcrumb">
            <ol className="breadcrumbs">{breadcrumbsWithSeparator}</ol>
        </nav>
    );
}
